import { useEffect, useState } from 'react';
import parentsHero1 from './assets/news/news1.jpg';
import parentsHero2 from './assets/news/news5.jpg';
import parentsHero3 from './assets/news/news8.jpg';
import parentsImage1 from './assets/news/news3.jpg';
import parentsImage2 from './assets/news/news4.jpg';
import parentsImage3 from './assets/news/news7.jpg';

function ParentsPage({ page, onNavigate }) {
  const heroSlides = page?.hero_slides || [parentsHero1, parentsHero2, parentsHero3];
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveSlide((current) => (current + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(intervalId);
  }, [heroSlides.length]);

  useEffect(() => {
    const animatedItems = document.querySelectorAll('.parents-animate');
    if (!animatedItems.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    animatedItems.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  const heroTitle = page?.title || 'Parents';
  const heroSummary =
    page?.summary ||
    'Strengthening the partnership between families and school through meaningful collaboration.';

  const palc = page?.parents_learning_community || [
    {
      id: 'palc-support',
      title: 'Family Support Circles',
      description:
        'Monthly gatherings to connect families, share insights, and build a supportive parent network.',
      image: parentsImage1,
    },
    {
      id: 'palc-workshops',
      title: 'Learning Workshops',
      description:
        'Interactive sessions on learning strategies, wellbeing, and student development at home.',
      image: parentsImage2,
    },
    {
      id: 'palc-community',
      title: 'Community Collaboration',
      description:
        'Joint initiatives that connect parents with school programs, events, and service projects.',
      image: parentsImage3,
    },
  ];

  return (
    <section className="parents-page">
      <section className="admissions-hero" id="parents-overview">
        <div className="admissions-hero-media" aria-hidden="true">
          {heroSlides.map((slide, index) => (
            <div
              key={slide}
              className={`admissions-hero-slide ${index === activeSlide ? 'active' : ''}`}
            >
              <img src={slide} alt="Parents hero" />
            </div>
          ))}
        </div>
        <div className="admissions-hero-overlay" aria-hidden="true" />
        <div className="admissions-hero-content">
          <p className="admissions-hero-kicker">Parents</p>
          <h2>{heroTitle}</h2>
          <p>{heroSummary}</p>
          <div className="admissions-hero-dots" role="tablist" aria-label="Parents hero slides">
            {heroSlides.map((slide, index) => (
              <button
                key={slide}
                type="button"
                className={`admissions-hero-dot ${index === activeSlide ? 'active' : ''}`}
                onClick={() => setActiveSlide(index)}
                aria-label={`Show slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="parents-palc" id="parents-learning-community">
        <div className="parents-palc-container">
          {/* Header Section */}
          <div className="parents-palc-header parents-animate">
            <div className="parents-palc-header-content">
              <p className="parents-palc-kicker">Parents Learning Community</p>
              <h2>Growing Together Through Shared Learning & Support</h2>
              <p className="parents-palc-subtitle">
                A vibrant collaborative space where families connect, learn, and actively contribute to our school community. We believe that strong parent engagement creates the foundation for student success.
              </p>
            </div>
            <div className="parents-palc-stats">
              <div className="palc-stat">
                <div className="palc-stat-number">500+</div>
                <div className="palc-stat-label">Active Parents</div>
              </div>
              <div className="palc-stat">
                <div className="palc-stat-number">24</div>
                <div className="palc-stat-label">Annual Events</div>
              </div>
              <div className="palc-stat">
                <div className="palc-stat-number">12</div>
                <div className="palc-stat-label">Learning Programs</div>
              </div>
            </div>
          </div>

          {/* Featured Program */}
          {palc[0] && (
            <article
              key={palc[0].id}
              id={palc[0].id}
              className="parents-palc-featured parents-animate"
              onClick={() => onNavigate && onNavigate(`palc-${palc[0].id}`)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  onNavigate && onNavigate(`palc-${palc[0].id}`);
                }
              }}
            >
              <div className="palc-featured-media">
                <img src={palc[0].image} alt={palc[0].title} />
                <div className="palc-featured-overlay" />
                <div className="palc-featured-badge">Featured</div>
              </div>
              <div className="palc-featured-content">
                <p className="palc-featured-label">Signature Program</p>
                <h3>{palc[0].title}</h3>
                <p className="palc-featured-description">{palc[0].description}</p>
                <div className="palc-featured-benefits">
                  <span><i className="fas fa-check-circle" /> Monthly gatherings</span>
                  <span><i className="fas fa-check-circle" /> Peer support network</span>
                  <span><i className="fas fa-check-circle" /> Expert facilitators</span>
                </div>
                <button className="palc-featured-cta">
                  Learn More <i className="fas fa-arrow-right" />
                </button>
              </div>
            </article>
          )}

          {/* Programs Grid */}
          <div className="parents-palc-programs">
            {palc.slice(1).map((item, index) => (
              <article
                key={item.id}
                id={item.id}
                className="palc-program-card parents-animate"
                onClick={() => onNavigate && onNavigate(`palc-${item.id}`)}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    onNavigate && onNavigate(`palc-${item.id}`);
                  }
                }}
              >
                <div className="palc-program-media">
                  <img src={item.image} alt={item.title} />
                  <div className="palc-program-overlay" />
                  <div className="palc-program-icon">
                    <i className={`fas ${index === 0 ? 'fa-users' : 'fa-handshake'}`} />
                  </div>
                </div>
                <div className="palc-program-body">
                  <div className="palc-program-number">0{index + 2}</div>
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                  <div className="palc-program-footer">
                    <button type="button" className="palc-program-link">
                      Explore <i className="fas fa-arrow-right" />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Benefits Section */}
          <div className="parents-palc-benefits parents-animate">
            <h3>Why Join PaLC?</h3>
            <div className="palc-benefits-grid">
              <div className="palc-benefit-item">
                <div className="palc-benefit-icon">
                  <i className="fas fa-lightbulb" />
                </div>
                <h4>Continuous Learning</h4>
                <p>Access workshops and resources on parenting, child development, and educational strategies.</p>
              </div>
              <div className="palc-benefit-item">
                <div className="palc-benefit-icon">
                  <i className="fas fa-people-group" />
                </div>
                <h4>Community Connection</h4>
                <p>Build meaningful relationships with other families and create a supportive parent network.</p>
              </div>
              <div className="palc-benefit-item">
                <div className="palc-benefit-icon">
                  <i className="fas fa-heart" />
                </div>
                <h4>Student Success</h4>
                <p>Strengthen home-school partnership to directly support your child's academic and personal growth.</p>
              </div>
              <div className="palc-benefit-item">
                <div className="palc-benefit-icon">
                  <i className="fas fa-star" />
                </div>
                <h4>Leadership Opportunities</h4>
                <p>Volunteer, lead initiatives, and make a meaningful impact on our school community.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="handbook-section" id="parent-student-handbook">
        <div className="handbook-wrapper">
          {/* Header */}
          <div className="handbook-header-block parents-animate">
            <p className="handbook-section-kicker">Parent & Student Handbook</p>
            <h2 className="handbook-section-title">Essential Guide to School Life</h2>
            <p className="handbook-section-subtitle">
              Complete resource for policies, schedules, and everything families need to know to support your child's success
            </p>
          </div>

          {/* Main Featured Card */}
          <div className="handbook-featured-card parents-animate">
            {/* Left Column: Image */}
            <div className="handbook-left-column">
              {/* Image Box */}
              <div className="handbook-featured-image">
                <img 
                  src={parentsImage2} 
                  alt="Parent & Student Handbook" 
                  className="handbook-image"
                />
                <div className="handbook-image-overlay" />
                <div className="handbook-pdf-badge">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  <span>PDF</span>
                </div>
              </div>
            </div>

            {/* Right Column: Content */}
            <div className="handbook-featured-content">
              <p className="handbook-featured-label">Download Now</p>
              <h3 className="handbook-featured-title">Parent & Student Handbook</h3>
              <p className="handbook-featured-description">
                Your complete guide to school policies, daily schedules, academic standards, and support services—all in one comprehensive downloadable resource.
              </p>

              {/* Key Features */}
              <div className="handbook-key-features">
                <div className="key-feature">
                  <i className="fas fa-check-circle"></i>
                  <span>School Policies & Guidelines</span>
                </div>
                <div className="key-feature">
                  <i className="fas fa-check-circle"></i>
                  <span>Daily Schedules & Routines</span>
                </div>
                <div className="key-feature">
                  <i className="fas fa-check-circle"></i>
                  <span>Academic Standards & Grading</span>
                </div>
                <div className="key-feature">
                  <i className="fas fa-check-circle"></i>
                  <span>Student Wellbeing Resources</span>
                </div>
              </div>

              {/* File Info */}
              <div className="handbook-file-details">
                <span><i className="fas fa-file-pdf"></i> 2.4 MB</span>
                <span><i className="fas fa-file-lines"></i> 48 Pages</span>
                <span><i className="fas fa-calendar"></i> May 2026</span>
              </div>

              {/* Download Button */}
              <button className="handbook-download-cta">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                <span>Download Handbook</span>
              </button>
            </div>
          </div>

          {/* What's Inside Grid */}
          <div className="handbook-whats-inside parents-animate">
            <h3 className="whats-inside-title">What's Inside</h3>
            <div className="whats-inside-grid">
              <div className="inside-item">
                <div className="inside-icon">
                  <i className="fas fa-book"></i>
                </div>
                <h4>School Policies</h4>
                <p>Attendance, code of conduct, academic integrity, and dress code</p>
              </div>

              <div className="inside-item">
                <div className="inside-icon">
                  <i className="fas fa-calendar-alt"></i>
                </div>
                <h4>Schedules & Routines</h4>
                <p>Daily schedules, break times, assembly times, and special events</p>
              </div>

              <div className="inside-item">
                <div className="inside-icon">
                  <i className="fas fa-chart-line"></i>
                </div>
                <h4>Academic Information</h4>
                <p>Grading system, assessment methods, report cards, and meetings</p>
              </div>

              <div className="inside-item">
                <div className="inside-icon">
                  <i className="fas fa-heart"></i>
                </div>
                <h4>Student Wellbeing</h4>
                <p>Counseling services, health & safety, and support programs</p>
              </div>

              <div className="inside-item">
                <div className="inside-icon">
                  <i className="fas fa-shield-alt"></i>
                </div>
                <h4>Safety & Security</h4>
                <p>Emergency procedures, campus safety, and security measures</p>
              </div>

              <div className="inside-item">
                <div className="inside-icon">
                  <i className="fas fa-address-book"></i>
                </div>
                <h4>Contact Directory</h4>
                <p>Staff contacts, department numbers, and emergency procedures</p>
              </div>
            </div>
          </div>

        </div>
      </section>

      <section className="parents-portal" id="parent-portal-access">
        <div className="portal-wrapper">
          {/* Header */}
          <div className="portal-header parents-animate">
            <p className="portal-kicker">Parent Portal Access</p>
            <h2 className="portal-title">Stay Connected & Informed</h2>
            <p className="portal-subtitle">
              Access real-time updates on your child's progress, grades, schedules, and learning resources through our secure LMS portal
            </p>
          </div>

          {/* Main Portal Card */}
          <div className="portal-main-card parents-animate">
            {/* Left: Features */}
            <div className="portal-features-list">
              <div className="portal-feature-item">
                <div className="feature-icon">
                  <i className="fas fa-chart-line"></i>
                </div>
                <div className="feature-content">
                  <h4>Real-Time Progress</h4>
                  <p>Monitor your child's academic performance with instant updates</p>
                </div>
              </div>

              <div className="portal-feature-item">
                <div className="feature-icon">
                  <i className="fas fa-calendar-check"></i>
                </div>
                <div className="feature-content">
                  <h4>Schedule & Events</h4>
                  <p>Never miss important dates with integrated calendar</p>
                </div>
              </div>

              <div className="portal-feature-item">
                <div className="feature-icon">
                  <i className="fas fa-lock"></i>
                </div>
                <div className="feature-content">
                  <h4>Secure Access</h4>
                  <p>Enterprise-grade security for your child's information</p>
                </div>
              </div>

              <div className="portal-feature-item">
                <div className="feature-icon">
                  <i className="fas fa-mobile-alt"></i>
                </div>
                <div className="feature-content">
                  <h4>Mobile Friendly</h4>
                  <p>Access anytime, anywhere from any device</p>
                </div>
              </div>
            </div>

            {/* Right: CTA */}
            <div className="portal-cta-box">
              <div className="cta-icon-large">
                <i className="fas fa-laptop"></i>
              </div>
              <h3>Access LMS Portal</h3>
              <p>Get instant access to grades, schedules, and learning resources</p>
              <a href="https://classroom.google.com" className="portal-cta-button" target="_blank" rel="noopener noreferrer">
                <i className="fas fa-sign-in-alt"></i>
                Launch Portal
              </a>
              <p className="portal-cta-note">Secure login • 24/7 Access</p>
            </div>
          </div>
        </div>
      </section>

      <section className="parents-communication" id="communication-channels">
        <div className="communication-wrapper">
          {/* Header */}
          <div className="communication-header parents-animate">
            <p className="communication-kicker">Communication Channels</p>
            <h2 className="communication-title">Multiple Ways to Stay Connected</h2>
            <p className="communication-subtitle">
              We believe in open communication. Connect with us through your preferred channel
            </p>
          </div>

          {/* Communication Channels Grid */}
          <div className="communication-channels-grid parents-animate">
            {/* Google Classroom */}
            <div className="communication-card">
              <div className="channel-icon">
                <i className="fas fa-chalkboard-user"></i>
              </div>
              <h3>Google Classroom</h3>
              <p>Daily class updates, assignments, and quick teacher communications</p>
              <ul className="channel-features">
                <li><i className="fas fa-check-circle"></i> Daily Updates</li>
                <li><i className="fas fa-check-circle"></i> Assignment Tracking</li>
                <li><i className="fas fa-check-circle"></i> Direct Messaging</li>
              </ul>
              <a href="https://classroom.google.com" className="channel-link" target="_blank" rel="noopener noreferrer">
                Access Classroom <i className="fas fa-arrow-right"></i>
              </a>
            </div>

            {/* Email Updates */}
            <div className="communication-card">
              <div className="channel-icon">
                <i className="fas fa-envelope"></i>
              </div>
              <h3>Email Updates</h3>
              <p>Weekly summaries, important reminders, and event invitations</p>
              <ul className="channel-features">
                <li><i className="fas fa-check-circle"></i> Weekly Digest</li>
                <li><i className="fas fa-check-circle"></i> Event Reminders</li>
                <li><i className="fas fa-check-circle"></i> Important Notices</li>
              </ul>
              <a href="mailto:support@school.com" className="channel-link">
                Subscribe Now <i className="fas fa-arrow-right"></i>
              </a>
            </div>

            {/* LMS Portal */}
            <div className="communication-card">
              <div className="channel-icon">
                <i className="fas fa-laptop"></i>
              </div>
              <h3>LMS Portal</h3>
              <p>Central learning platform for resources, grades, and progress tracking</p>
              <ul className="channel-features">
                <li><i className="fas fa-check-circle"></i> Learning Resources</li>
                <li><i className="fas fa-check-circle"></i> Grade Tracking</li>
                <li><i className="fas fa-check-circle"></i> Progress Reports</li>
              </ul>
              <a href="/lms" className="channel-link">
                Open Portal <i className="fas fa-arrow-right"></i>
              </a>
            </div>
          </div>
        </div>
      </section>
    </section>
  );
}

export default ParentsPage;
